import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "Overview" },
  { to: "/risk", label: "Risk" },
  { to: "/markets", label: "Markets" },
  { to: "/the-markets", label: "The Markets" },
  { to: "/leverage", label: "Leverage" },
  { to: "/fellowship", label: "Fellowship" },
  { to: "/apply", label: "Apply" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const { user } = useAuth();
  const loc = useLocation();
  const portalHref = user?.role === "super_admin" ? "/super"
                    : user?.role === "admin" ? "/admin"
                    : user?.role === "client" ? "/portal" : "/login";
  return (
    <div className="min-h-screen flex flex-col relative">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="container-tight flex items-center justify-between h-16">
          <Link to="/" data-testid="brand-home-link" className="flex items-center gap-3">
            <div className="w-8 h-8 border border-primary/60 flex items-center justify-center">
              <span className="gold font-mono text-sm">P</span>
            </div>
            <div className="leading-none">
              <div className="text-lg tracking-tight">Piplight Capital</div>
              <div className="kicker text-[10px]">Quantitative Trading Division</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) => `link-nav ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link data-testid="nav-portal" to={portalHref}
              className="text-sm px-4 py-2 border border-border hover:border-primary transition-colors">
              {user ? "Portal" : "Client Login"}
            </Link>
            <Link data-testid="nav-apply-cta" to="/apply"
              className="btn-gold text-xs px-4 py-2 hidden sm:inline-flex items-center gap-2 uppercase tracking-widest">
              Apply <ArrowUpRight strokeWidth={1.6} size={14} />
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="border-t border-border mt-20">
        <div className="container-tight py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="kicker mb-3">Piplight Capital, LLC</div>
            <h3 className="text-3xl mb-4">Disciplined quantitative capital, a family of focused funds.</h3>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              Piplight Capital operates a quantitative trading division managing a suite of
              specialised funds. This platform is provided for the private review of prospective
              and existing investors.
            </p>
          </div>
          <div className="md:col-span-3">
            <div className="kicker mb-3">Navigate</div>
            <ul className="text-sm space-y-2">
              {links.map((l) => (<li key={l.to}><Link to={l.to} className="hover:gold">{l.label}</Link></li>))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="kicker mb-3">Legal</div>
            <ul className="text-sm space-y-2">
              <li><Link to="/privacy" className="hover:gold">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:gold">Terms & Conditions</Link></li>
            </ul>
            <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
              Nothing on this site constitutes an offer to sell or a solicitation to buy any security. Past performance is not indicative of future results. Investments in the funds involve significant risk, including loss of principal.
            </p>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="container-tight py-5 flex flex-col md:flex-row justify-between text-xs text-muted-foreground gap-2">
            <div>© {new Date().getFullYear()} Piplight Capital, LLC. All rights reserved.</div>
            <div className="font-mono">piplight.com · Private Preview</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
